import React from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Modal,
    ModalDialog,
    Typography,
} from '@mui/joy';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { fetchUserProfile, updateUserProfile } from '../redux/authReducer';

const getActionErrorMessage = (payload) => {
    if (typeof payload === 'string') return payload;
    if (payload && typeof payload.error === 'string') return payload.error;

    if (payload && typeof payload === 'object') {
        const firstValue = Object.values(payload)[0];
        if (typeof firstValue === 'string') return firstValue;
        if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') return firstValue[0];
    }

    return 'Failed to skip onboarding. Please try again.';
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useSelector(state => state.auth);
    const latestProfileRequestRef = React.useRef(0);
    const lastReadyUserIdRef = React.useRef(null);
    const [profileLoadState, setProfileLoadState] = React.useState('idle');
    const [showOnboardingDialog, setShowOnboardingDialog] = React.useState(false);
    const [isSkippingOnboarding, setIsSkippingOnboarding] = React.useState(false);
    const [skipOnboardingError, setSkipOnboardingError] = React.useState('');
    const normalizedUserId = user?.id != null ? String(user.id) : null;

    React.useEffect(() => {
        if (!isAuthenticated) {
            setProfileLoadState('idle');
            latestProfileRequestRef.current = 0;
            lastReadyUserIdRef.current = null;
            return;
        }

        if (!normalizedUserId) {
            setProfileLoadState('ready');
            return;
        }

        if (lastReadyUserIdRef.current === normalizedUserId) {
            setProfileLoadState('ready');
            return;
        }

        const requestId = latestProfileRequestRef.current + 1;
        latestProfileRequestRef.current = requestId;
        setProfileLoadState('loading');

        let isCancelled = false;

        (async () => {
            try {
                await dispatch(fetchUserProfile(normalizedUserId));
            } finally {
                if (isCancelled || latestProfileRequestRef.current !== requestId) {
                    return;
                }

                // Mark ready even on fetch failure to avoid deadlock on app entry.
                lastReadyUserIdRef.current = normalizedUserId;
                setProfileLoadState('ready');
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [dispatch, isAuthenticated, normalizedUserId]);

    const isProfileReady = !normalizedUserId || profileLoadState === 'ready';
    const needsAgreement = isProfileReady && user?.agreement === false;

    React.useEffect(() => {
        if (!isAuthenticated) {
            setShowOnboardingDialog(false);
            setSkipOnboardingError('');
            return;
        }

        if (!isProfileReady) return;

        if (needsAgreement) {
            setShowOnboardingDialog(false);
            setSkipOnboardingError('');
            return;
        }

        const justLoggedIn = location.state?.justLoggedIn === true;
        const fromAgreementAccepted = location.state?.fromAgreementAccepted === true;
        const needsOnboarding = user?.onboarding_completed === false;
        if ((!justLoggedIn && !fromAgreementAccepted) || !needsOnboarding) return;

        setShowOnboardingDialog(true);
        setSkipOnboardingError('');

        const nextState = { ...(location.state || {}) };
        delete nextState.justLoggedIn;
        delete nextState.fromAgreementAccepted;
        const hasState = Object.keys(nextState).length > 0;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: hasState ? nextState : null,
        });
    }, [
        isProfileReady,
        isAuthenticated,
        location.pathname,
        location.search,
        location.state,
        navigate,
        needsAgreement,
        user?.onboarding_completed,
    ]);

    const handleContinueSetup = React.useCallback(() => {
        setSkipOnboardingError('');
        setShowOnboardingDialog(false);
        navigate('/app/profile?tab=models&onboarding=1');
    }, [navigate]);

    const handleSkipOnboarding = React.useCallback(async () => {
        if (!normalizedUserId || isSkippingOnboarding) return;

        setIsSkippingOnboarding(true);
        setSkipOnboardingError('');

        try {
            const resultAction = await dispatch(
                updateUserProfile({
                    userId: normalizedUserId,
                    formData: { onboarding_completed: true },
                }),
            );

            if (updateUserProfile.fulfilled.match(resultAction)) {
                await dispatch(fetchUserProfile(normalizedUserId));
                setShowOnboardingDialog(false);
                navigate('/app/tasks', { replace: true });
                return;
            }

            if (updateUserProfile.rejected.match(resultAction)) {
                setSkipOnboardingError(getActionErrorMessage(resultAction.payload));
                return;
            }

            setSkipOnboardingError('Failed to skip onboarding. Please try again.');
        } catch (error) {
            setSkipOnboardingError('Failed to skip onboarding. Please try again.');
        } finally {
            setIsSkippingOnboarding(false);
        }
    }, [dispatch, isSkippingOnboarding, navigate, normalizedUserId]);

    if (!isAuthenticated) return <Navigate to="/login" />;
    if (needsAgreement) return <Navigate to="/user-agreement" replace />;
    if (adminOnly && user.role !== 'admin') return <Navigate to="/app/tasks" />;

    return (
        <>
            {children}
            <Modal open={showOnboardingDialog} onClose={() => {}}>
                <ModalDialog role="alertdialog" aria-labelledby="onboarding-dialog-title">
                    <DialogTitle id="onboarding-dialog-title">Welcome to the Document Compliance System!</DialogTitle>
                    <DialogContent>
                        <Typography level="body-sm">
                            If you're new here, we've created a step-by-step onboarding guide on how to use the
                            system. Do you want to start now, or are you already familiar with the system?
                        </Typography>
                        {skipOnboardingError && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
                                {skipOnboardingError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="soft"
                            color="primary"
                            onClick={handleContinueSetup}
                            disabled={isSkippingOnboarding}
                        >
                            Start onboarding
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={handleSkipOnboarding}
                            loading={isSkippingOnboarding}
                            disabled={isSkippingOnboarding}
                        >
                            I'm familiar with the system
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </>
    );
};

export default ProtectedRoute;
